import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import AdminTabBar from '../components/AdminTabBar';
import { adminGetPerformer, adminUpdatePerformer, getGenres } from '../services/api';

// Admin-side edit of a performer's CORE profile fields, for tidying up
// registrations that weren't completed correctly. Instruments / media /
// availability / stage requirements stay performer-managed (not shown here).

const TYPE_OPTIONS = [
  { value: '',            label: '— select —'  },
  { value: 'solo',        label: 'Solo'        },
  { value: 'duo',         label: 'Duo'         },
  { value: 'band',        label: 'Band'        },
  { value: 'dj',          label: 'DJ'          },
  { value: 'spoken_word', label: 'Spoken Word' },
  { value: 'other',       label: 'Other'       },
];

const SOCIAL_FIELDS = [
  { key: 'website',    label: 'Website'    },
  { key: 'instagram',  label: 'Instagram'  },
  { key: 'facebook',   label: 'Facebook'   },
  { key: 'spotify',    label: 'Spotify'    },
  { key: 'soundcloud', label: 'SoundCloud' },
  { key: 'youtube',    label: 'YouTube'    },
  { key: 'bandcamp',   label: 'Bandcamp'   },
];

const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
  borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--navy)', marginBottom: '4px' };
const rowStyle = { marginBottom: '1rem' };

export default function AdminPerformerEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [email, setEmail]     = useState('');
  const [allGenres, setAllGenres] = useState([]);
  const [genreIds, setGenreIds]   = useState([]);
  const [form, setForm] = useState({
    real_name: '', stage_name: '', bio: '', phone: '',
    performance_type: '', set_length_min: '', set_length_max: '',
    website: '', instagram: '', facebook: '', spotify: '',
    soundcloud: '', youtube: '', bandcamp: '', is_scala_member: false,
  });

  useEffect(() => {
    Promise.all([adminGetPerformer(id), getGenres()])
      .then(([pRes, gRes]) => {
        const p = pRes.data || {};
        setEmail(p.email || '');
        setForm({
          real_name: p.real_name || '',
          stage_name: p.stage_name || '',
          bio: p.bio || '',
          phone: p.phone || '',
          performance_type: p.performance_type || '',
          set_length_min: p.set_length_min ?? '',
          set_length_max: p.set_length_max ?? '',
          website: p.website || '',
          instagram: p.instagram || '',
          facebook: p.facebook || '',
          spotify: p.spotify || '',
          soundcloud: p.soundcloud || '',
          youtube: p.youtube || '',
          bandcamp: p.bandcamp || '',
          is_scala_member: !!p.is_scala_member,
        });
        setGenreIds((p.genres || []).map(g => g.id));
        setAllGenres(gRes.data || []);
      })
      .catch(() => setError('Failed to load performer.'))
      .finally(() => setLoading(false));
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleGenre = (gid) =>
    setGenreIds(ids => ids.includes(gid) ? ids.filter(x => x !== gid) : [...ids, gid]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSaveMsg('');
    try {
      await adminUpdatePerformer(id, { ...form, genre_ids: genreIds });
      setSaveMsg('Saved.');
      setTimeout(() => navigate(`/admin/performers/${id}`), 600);
    } catch (err) {
      setError(err?.response?.data?.error || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <AdminTabBar />
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1rem' }}>
        <Link to={`/admin/performers/${id}`} className="a-btn a-btn--ghost a-btn--sm">← Back</Link>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--navy)', margin: '1rem 0 0.25rem' }}>
          Edit Performer
        </h1>
        {email && <div style={{ color: 'var(--muted)', fontSize: '13px', marginBottom: '1.5rem' }}>{email}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '3rem' }}>Loading…</div>
        ) : (
          <form onSubmit={handleSave} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '1.5rem' }}>
            {error && <div className="a-error" style={{ marginBottom: '1rem' }}>{error}</div>}

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ ...rowStyle, flex: 1 }}>
                <label style={labelStyle}>Real name</label>
                <input style={inputStyle} value={form.real_name} onChange={e => set('real_name', e.target.value)} />
              </div>
              <div style={{ ...rowStyle, flex: 1 }}>
                <label style={labelStyle}>Stage name</label>
                <input style={inputStyle} value={form.stage_name} onChange={e => set('stage_name', e.target.value)} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ ...rowStyle, flex: 1 }}>
                <label style={labelStyle}>Phone</label>
                <input style={inputStyle} value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
              <div style={{ ...rowStyle, flex: 1 }}>
                <label style={labelStyle}>Performance type</label>
                <select style={inputStyle} value={form.performance_type} onChange={e => set('performance_type', e.target.value)}>
                  {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ ...rowStyle, flex: 1 }}>
                <label style={labelStyle}>Set length min (mins)</label>
                <input type="number" min="0" style={inputStyle} value={form.set_length_min} onChange={e => set('set_length_min', e.target.value)} />
              </div>
              <div style={{ ...rowStyle, flex: 1 }}>
                <label style={labelStyle}>Set length max (mins)</label>
                <input type="number" min="0" style={inputStyle} value={form.set_length_max} onChange={e => set('set_length_max', e.target.value)} />
              </div>
            </div>

            <div style={rowStyle}>
              <label style={labelStyle}>Bio <span style={{ color: 'var(--muted)', fontWeight: 400 }}>({form.bio.length}/500)</span></label>
              <textarea style={{ ...inputStyle, minHeight: '90px', resize: 'vertical' }} maxLength={500}
                value={form.bio} onChange={e => set('bio', e.target.value)} />
            </div>

            <div style={rowStyle}>
              <label style={labelStyle}>Genres</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {allGenres.length === 0 && <span style={{ color: 'var(--muted)', fontSize: '13px' }}>No genres available.</span>}
                {allGenres.map(g => {
                  const on = genreIds.includes(g.id);
                  return (
                    <button type="button" key={g.id} onClick={() => toggleGenre(g.id)}
                      style={{
                        padding: '5px 12px', borderRadius: '9999px', cursor: 'pointer',
                        border: on ? '1px solid var(--navy)' : '1px solid #d1d5db',
                        background: on ? 'var(--navy)' : '#fff',
                        color: on ? '#fff' : 'var(--text)', fontSize: '13px',
                      }}>
                      {g.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ ...rowStyle, marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
              <label style={labelStyle}>Links</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {SOCIAL_FIELDS.map(f => (
                  <div key={f.key}>
                    <label style={{ ...labelStyle, fontWeight: 400, color: 'var(--muted)' }}>{f.label}</label>
                    <input style={inputStyle} value={form[f.key]} onChange={e => set(f.key, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ ...rowStyle, marginTop: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '14px' }}>
                <input type="checkbox" checked={form.is_scala_member} onChange={e => set('is_scala_member', e.target.checked)} />
                SCALA member
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button type="submit" className="a-btn a-btn--navy" disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className="a-btn a-btn--ghost" onClick={() => navigate(`/admin/performers/${id}`)}>
                Cancel
              </button>
              {saveMsg && <span style={{ color: 'green', fontSize: '14px' }}>{saveMsg}</span>}
            </div>
          </form>
        )}
      </div>
    </>
  );
}
